import { toast } from 'sonner'
import { clientAppLaunchBlockedReason } from '@/shared/config/client-app-launch'
import { getErrorMessage } from '@/shared/utils/handle-api-error'
import { useReleaseMobileAccessMutation } from './use-mobile-access'

/**
 * Liberar el acceso móvil del cliente DESPUÉS de que la venta cerró (ADR 0024).
 *
 * ── POR QUÉ ES UNA SEGUNDA LLAMADA Y NO PARTE DEL AVANCE ────────────────────
 *
 * Es deliberado: emitirlo dentro de la transacción de aprobación haría que un correo mal cargado
 * tumbe el cierre de una venta. El acceso importa; la venta importa más. Y por eso mismo un fallo
 * acá **no se propaga**: la venta ya está cerrada, se avisa y se sigue. Lo que queda no es un error
 * perdido — la tarjeta del expediente muestra el pendiente y su botón dispara esta misma acción.
 *
 * ── POR QUÉ ES UN HOOK COMPARTIDO ───────────────────────────────────────────
 *
 * ☠️ Porque **había dos botones que cierran una venta y sólo uno liberaba el acceso.** El del
 * expediente ofrecía la casilla y llamaba acá; el de las acciones del panel del tablero —agregado
 * después— hacía la misma transición y **no liberaba nada**. Misma operación, dos entradas,
 * distinto resultado para el comprador: se queda sin ver su inmueble en la app, sin que nadie reciba
 * un error.
 *
 * Es el mismo patrón que ya apareció cuatro veces en esta migración —un par de piezas que hacen lo
 * mismo con distinta madurez, y la que no se vuelve a tocar se queda atrás—, así que el arreglo no
 * fue copiar la función al panel sino sacarla de los dos.
 *
 * ── Y POR QUÉ NO LLAMA NADA SIN LANZAMIENTO ─────────────────────────────────
 *
 * Con la app sin publicar no se hace ninguna llamada: el servidor contestaría
 * `409 CLIENT_APP_NOT_LAUNCHED` y el toast diría que la venta se autorizó pero el acceso falló,
 * cuando no falló nada: todavía no se puede. El diálogo de la aprobación ya no deja prender el switch, así que esto es
 * la segunda llave del mismo candado: ninguna entrada futura puede saltearlo. La aprobación NUNCA
 * depende de esto.
 */
export function useReleaseAccessAfterApproval(publicId: string | null | undefined) {
  const releaseMobileAccess = useReleaseMobileAccessMutation(publicId ?? null)

  return async () => {
    if (clientAppLaunchBlockedReason() !== null) return

    if (!publicId) {
      toast.warning(
        'La negociación no tiene identificador público, así que el acceso no se liberó automáticamente. Hazlo desde el expediente.'
      )
      return
    }

    try {
      // `undefined` EXPLÍCITO: sin dueño nombrado, la liberación alcanza a quien diga la
      // política. Nombrar a alguien acá sería saltearla, y este camino es el automático.
      const result = await releaseMobileAccess.mutateAsync(undefined)
      if (result.owners.length === 0) return

      // Los copropietarios fuera de alcance no son un pendiente (decisión Nº 28): se
      // habilitan a pedido desde su fila. Contarlos haría que aprobar una venta con
      // copropietarios —el 44% de los expedientes— avisara siempre que algo quedó a medias.
      const enAlcance = result.owners.filter((owner) => owner.reason !== 'not-in-scope')
      const granted = enAlcance.filter((owner) => owner.status === 'granted').length
      const pending = enAlcance.length - granted

      if (pending === 0) {
        toast.success('Acceso en la app liberado para los propietarios.')
      } else {
        toast.warning(
          `Acceso liberado para ${granted} de ${enAlcance.length} propietarios. Revisa los pendientes en el expediente.`
        )
      }
    } catch (error) {
      toast.error(
        `La negociación se autorizó, pero el acceso no se liberó: ${getErrorMessage(error)}`
      )
    }
  }
}
